import {
  AddOutlined,
  CancelOutlined,
  EditOutlined,
  EqualizerOutlined,
  ExpandMoreRounded,
  LockOpenOutlined,
} from "@mui/icons-material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { Button, Chip, Grid, MenuItem, Select, Stack, Typography } from "@mui/material";
import {
  GridActionsCellItem,
  GridActionsCellItemProps,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  GridValidRowModel,
} from "@mui/x-data-grid-premium";
import React from "react";
import { useTranslation } from "react-i18next";
import { ExternalAccessExpiresIn } from "../../Components/Access/ExternalAccess/external-access-expires-in";
import ExternalAccessPermissionSummary from "../../Components/Access/ExternalAccess/external-access-permission-summary";
import { ExternalAccessStatusTableCell } from "../../Components/Access/ExternalAccess/external-access-status";
import AssetThumbnail from "../../Components/Misc/AssetThumbnail";
import ListPageHeader, { ListPageHeaderLeft, ListPageHeaderRight } from "../../Components/Misc/ListPageHeader";
import { SearchInput } from "../../Components/Misc/SearchExpand/search-expand";
import { ModalContext } from "../../Components/Modals";
import ModalConfirmAction, {
  ModalConfirmActionElementProps,
} from "../../Components/Modals/ModalConfirmAction/modal-confirm-action";
import ModalCreateOrUpdateExternalAccess, {
  ModalCreateOrUpdateExternalAccessElementProps,
} from "../../Components/Modals/ModalCreateOrUpdateExternalAccess/modal-create-or-update-external-access";
import ExternalAccessPanel, {
  CustomExternalAccessDto,
} from "../../Components/Panels/AccessPanel/external-access-panel";
import TableCellChip from "../../Components/Table/TableCellChip/table-cell-chip";
import TableSkeleton from "../../Components/Table/TableSkeleton";
import DataTable, { CellSize } from "../../Components/Table/table";
import { ExternalAccessDto, ExternalAccessStatus } from "../../Core/Api/Types/access-models";
import { DbUserBasicInfos } from "../../Core/Api/Types/db-user-basic-infos";
import { ObjectType } from "../../Core/Api/Types/object-type";
import { getPermissionReadableName, PermissionType } from "../../Core/Api/Types/permission-type";
import { accessService, assetService, userService } from "../../Core/Api/services";
import GlobalConst from "../../Core/Utils/Constants/globals";
import useCustomSnackbar from "../../Utils/Hooks/useCustomSnackbar";
import { AclTypes } from "../../Core/Api/Types/acl-types";
import moment from "moment";
import { getExpirationDaysFromString, UserHasAcl } from "../../Utils/helpers";
import TableCellDate2Lines from "../../Components/Table/TableCellDate2Lines";
import TableUuids from "../../Components/Table/table-uuids";

const DriveExternalAccess = () => {
  const { t } = useTranslation();
  const enqueueSnackbar = useCustomSnackbar();
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const userCanCreateOrUpdate = UserHasAcl(AclTypes.aclExternalAccessCreateOrUpdate);
  const userCanRevoke = UserHasAcl(AclTypes.aclExternalAccessRevoke);

  const [isLoading, setIsLoading] = React.useState(true);
  const [allUsers, setAllUsers] = React.useState<DbUserBasicInfos[]>([]);
  const [externalAccesses, setExternalAccesses] = React.useState<ExternalAccessDto[]>([]);
  const [panelOpen, setPanelOpen] = React.useState<boolean>(false);
  const [selectedAccess, setSelectedAccess] = React.useState<CustomExternalAccessDto>();

  const [filterText, setFilterText] = React.useState("");
  const [filterPermissions, setFilterPermissions] = React.useState<PermissionType[]>([]);
  const [filterStatus, setFilterStatus] = React.useState<ExternalAccessStatus[]>([]);

  type Row = (typeof externalAccesses)[number];

  const columns: GridColDef[] = [
    {
      field: "objectName",
      headerName: t("common:item"),
      type: "string",
      renderCell: (params: GridRenderCellParams<Row>) => (
        <CustomItemTableCell
          type={params?.row?.objectType ?? ObjectType.Unknown}
          name={params?.row?.objectName ?? ""}
        />
      ),
      minWidth: CellSize.xlarge,
      flex: CellSize.xlarge,
    },
    {
      field: "objectChildCount",
      headerName: t("table:headerName.itemsCount"),
      type: "number",
      headerAlign: "left",
      align: "left",
      valueGetter: (params: any, row: Row) => row?.objectChildCount ?? 0 + 1,
      renderCell: (params: GridRenderCellParams<Row>) => <Chip label={params.value} />,
      minWidth: CellSize.small,
      flex: CellSize.small,
    },
    {
      field: "email",
      headerName: t("common:email"),
      type: "string",
      renderCell: (params: GridRenderCellParams<Row>) => <TableCellChip data={params} />,
      minWidth: CellSize.large,
      flex: CellSize.large,
    },
    {
      field: "permissions",
      headerName: t("common:permissions"),
      type: "singleSelect",
      valueOptions: GlobalConst.EXTERNAL_ACCESS_ALLOWED_PERMISSIONS.sort().map((permission) => ({
        value: permission,
        label: getPermissionReadableName(permission),
      })),
      renderCell: (params: GridRenderCellParams<Row>) => <ExternalAccessPermissionSummary permissions={params.value} />,
      minWidth: CellSize.medium,
      flex: CellSize.medium,
    },
    {
      field: "status",
      headerName: t("common:status"),
      type: "singleSelect",
      valueOptions: Object.values(ExternalAccessStatus),
      renderCell: (params: GridRenderCellParams<Row>) => (
        <ExternalAccessStatusTableCell status={params.value as ExternalAccessStatus} />
      ),
      minWidth: CellSize.small,
      flex: CellSize.small,
    },
    {
      field: "expirationDate",
      headerName: t("table:headerName.expiresIn"),
      type: "number",
      headerAlign: "left",
      align: "left",
      valueGetter: (params: string, row: Row) => getExpirationDaysFromString(params),
      renderCell: (params: GridRenderCellParams<Row>) => (
        <ExternalAccessExpiresIn nbDays={params.value} status={params?.row?.status} />
      ),
      minWidth: CellSize.small,
      flex: CellSize.small,
    },
    {
      field: "ownerId",
      headerName: t("common:createdBy"),
      type: "string",
      valueGetter: (params: DbUserBasicInfos["id"], row: Row) => getUserName(params),
      minWidth: CellSize.small,
      flex: CellSize.small,
    },
    {
      field: "createdAt",
      headerName: t("common:createdAt"),
      type: "date",
      minWidth: CellSize.small,
      flex: CellSize.small,
      valueGetter: (value: string, row: Row) => new Date(value),
      renderCell: (params: GridRenderCellParams<Row>) => <TableCellDate2Lines date={params.value} />,
    },
    {
      field: "updatedAt",
      headerName: t("common:updatedOn"),
      type: "date",
      minWidth: CellSize.small,
      flex: CellSize.small,
      valueGetter: (value: Date, row: Row) => (row.updatedAt ? new Date(row.updatedAt) : null),
      renderCell: (params: GridRenderCellParams<Row>) =>
        params.value ? <TableCellDate2Lines date={params.value} /> : null,
    },
    {
      field: "actions",
      headerName: "",
      type: "actions",
      align: "right",
      getActions: (params) => {
        const output: React.ReactElement<GridActionsCellItemProps>[] = [];
        if (
          params?.row?.status === ExternalAccessStatus.Active ||
          params?.row?.status === ExternalAccessStatus.Pending
        ) {
          if (userCanCreateOrUpdate) {
            output.push(
              <GridActionsCellItem
                icon={<EditOutlined />}
                onClick={() => handleEditClick(params?.row?.id)}
                label={t("common:editUpperCase")}
                showInMenu
              />
            );
          }
          if (userCanRevoke) {
            output.push(
              <GridActionsCellItem
                icon={<CancelOutlined />}
                onClick={() => handleRevokeClick(params?.row?.id)}
                label={t("table:cellLabel.revoke")}
                color="error"
                showInMenu
              />
            );
          }
        }
        return output;
      },
      minWidth: CellSize.actionsSmall,
      flex: CellSize.actionsSmall,
    },
  ];

  const filteredExternalAccesses = React.useMemo(() => {
    return externalAccesses
      .sort((a, b) => b.id - a.id)
      .filter((access) => {
        const permissionMatch =
          filterPermissions.length === 0 || filterPermissions.some((perm) => access.permissions.includes(perm));
        const statusMatch = filterStatus.length === 0 || filterStatus.includes(access.status);
        const textMatch =
          filterText === "" ||
          access.objectName?.includesAllWords(filterText) ||
          access.email.includesAllWords(filterText);
        return permissionMatch && statusMatch && textMatch;
      })
      .map((access) => ({
        ...access,
        objectType: access.objectType || ObjectType.Unknown,
        objectName: access.objectName || "Unknown",
        objectChildCount: access.objectChildCount || 0,
      }));
  }, [externalAccesses, filterPermissions, filterStatus, filterText]);

  const getUserName = (userId: number) => {
    const user = allUsers.find((user) => user.id === userId);
    if (!user) return "Unknown";
    return `${user.firstName} ${user.lastName}`.trim();
  };

  const handleCreateClick = () => {
    // Open create external access modal
    openModal &&
      openModal<ModalCreateOrUpdateExternalAccessElementProps>(ModalCreateOrUpdateExternalAccess, {
        onSuccess: loadExternalAccesses,
      });
  };

  const handleEditClick = async (id: number) => {
    const externalAccess = externalAccesses.find((access) => access.id === id);
    if (!externalAccess) {
      enqueueSnackbar(t("alert:error.accessNotFound"), "error");
      return;
    }

    const asset = (await assetService.GetObjectLightById(externalAccess.objectId, 0)).graph;
    if (!asset) {
      enqueueSnackbar(t("alert:error.assetNotFound"), "error");
      return;
    }

    openModal &&
      openModal<ModalCreateOrUpdateExternalAccessElementProps>(ModalCreateOrUpdateExternalAccess, {
        assets: [asset],
        accessToUpdate: externalAccess,
        onSuccess: loadExternalAccesses,
      });
  };

  const onConfirmDelete = (externalAccess: ExternalAccessDto) => {
    accessService.RevokeExternalAccess(externalAccess.id).then((response) => {
      if (response.success) {
        enqueueSnackbar(t("alert:success.accessRevoked"), "success");
        loadExternalAccesses();
      } else {
        enqueueSnackbar(response.message || t("alert:error.oopsSomethingWentWrongWithDot"), "error");
      }
    });
  };

  const handleRevokeClick = (id: number) => {
    const externalAccess = externalAccesses.find((access) => access.id === id);
    if (!externalAccess) {
      enqueueSnackbar(t("alert:error.accessNotFound"), "error");
      return;
    }

    openModal &&
      openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
        title: t("modal:title.confirmRevokeAccess", { email: externalAccess.email }),
        canConfirmWithoutComment: true,
        onConfirm: () => onConfirmDelete(externalAccess),
      });
  };

  const loadExternalAccesses = () => {
    accessService
      .ListExternalAccesses()
      .then((response) => {
        if (response.success && response.accesses) {
          setExternalAccesses(response.accesses);
        } else {
          enqueueSnackbar(response.message || t("alert:error.oopsSomethingWentWrongWithoutDot"), "error");
        }
      })
      .finally(() => setIsLoading(false));
  };

  const handleOnRowClick = (row: GridRowParams<ExternalAccessDto>) => {
    setSelectedAccess({ ...row.row, userName: getUserName(row.row.ownerId) } as CustomExternalAccessDto);
    setPanelOpen(true);
  };

  const selectedRows = React.useMemo(() => {
    if (selectedAccess) return [selectedAccess.id];
    return [];
  }, [selectedAccess]);

  const handleClosePanel = () => {
    setPanelOpen(false);
    setSelectedAccess(undefined);
  };

  React.useEffect(() => {
    // Fetch external accesses
    loadExternalAccesses();

    userService.GetAllUsersBasic().then((response) => {
      if (!response.success) {
        enqueueSnackbar(response.message || t("alert:error.oopsSomethingWentWrongWithoutDot"), "error");
      } else {
        setAllUsers(response.users || []);
      }
    });
  }, []);

  const additionalFilters = (
    <>
      <Select
        multiple
        value={filterPermissions}
        onChange={(value) => setFilterPermissions(value.target.value as PermissionType[])}
        IconComponent={ExpandMoreRounded}
        startAdornment={<LockOpenOutlined />}
        displayEmpty
        renderValue={(selected) =>
          selected.length === 0 ? "Permissions" : selected.map((perm) => getPermissionReadableName(perm)).join(", ")
        }
      >
        {GlobalConst.EXTERNAL_ACCESS_ALLOWED_PERMISSIONS.sort().map((permission) => (
          <MenuItem key={permission} value={permission}>
            {getPermissionReadableName(permission)}
          </MenuItem>
        ))}
      </Select>
      <Select
        multiple
        value={filterStatus}
        onChange={(value) => setFilterStatus(value.target.value as ExternalAccessStatus[])}
        IconComponent={ExpandMoreRounded}
        startAdornment={<EqualizerOutlined />}
        displayEmpty
        renderValue={(selected) => (selected.length === 0 ? "Statuses" : selected.join(", "))}
      >
        {Object.values(ExternalAccessStatus)
          .sort()
          .map((status) => (
            <MenuItem key={status} value={status}>
              {status}
            </MenuItem>
          ))}
      </Select>
    </>
  );

  const additionalActions = (
    <>
      {userCanCreateOrUpdate && (
        <Button color="primary" variant="contained" startIcon={<AddOutlined />} onClick={handleCreateClick}>
          {t("common:create")}
        </Button>
      )}
    </>
  );

  return (
    <Stack height="100%" px={2}>
      {!isLoading ? (
        <DataTable
          tableKey={TableUuids.DRIVE_EXTERNAL_ACCESS}
          columns={columns}
          rows={filteredExternalAccesses}
          additionalFilters={additionalFilters}
          additionalActions={additionalActions}
          onRowClick={handleOnRowClick}
          getRowHeight={() => "auto"}
          pinnedColumns={{ left: ["objectName"], right: ["actions"] }}
          selectedRows={selectedRows}
        />
      ) : (
        <TableSkeleton />
      )}

      {selectedAccess && (
        <ExternalAccessPanel open={panelOpen} accessDto={selectedAccess} closePanel={handleClosePanel} />
      )}
    </Stack>
  );
};

const CustomItemTableCell = (props: { type: ObjectType; name: string }) => {
  const { type, name } = props;

  return (
    <Grid container alignItems="center">
      <Grid item xs={2}>
        <AssetThumbnail type={type} size="small" />
      </Grid>
      <Grid item xs={10}>
        <Typography>{name}</Typography>
      </Grid>
    </Grid>
  );
};

export default DriveExternalAccess;
